import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/chart";
import {
  useAcknowledgeExperimentDiagnostic,
  useAcknowledgeExperimentDiagnosticDailySpend,
  useAudienceAndCampaignOptions,
  useExperimentCampaigns,
  useExperimentDiagnostics,
  useExperimentsDailyDelivery,
  usePredictionFlightOptions,
  useRunExperimentDiagnostics,
} from "@/http/api-hooks";
import {
  Experiment,
  ExperimentCampaign,
  ExperimentDiagnosticsMemberType,
  ExperimentDiagnosticsMetricType,
  ExperimentDiagnosticsSeries,
  ExperimentType,
} from "@/http/api-types";
import { cn, formatUTCDateTime, getAudienceSelectOptions } from "@/lib/utils";
import {
  buildCurrentDayDeliveryParamsForExperiment,
  buildDailyDeliveryMetricsForExperiment,
  buildDiagnosticsDailySpendMetrics,
  mergeDailyDeliveryDays,
} from "@/pages/experiments/daily-delivery-metrics";
import {
  DIAGNOSTIC_CHECKS,
  DiagnosticsCheckType,
  formatChartDayLabel,
  formatDiagnosticsMetricValue,
  getDiagnosticsCheckSummary,
  getDiagnosticsSeriesKey,
  isAcknowledgedDiagnosticsIssue,
  isDiagnosticsIssue,
  isOpenDiagnosticsIssue,
} from "@/pages/experiments/diagnostics";
import { AlertTriangle, CheckCircle2, Loader2, X } from "lucide-react";
import { type KeyboardEvent, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { toast } from "sonner";

const DIAGNOSTIC_SERIES_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(var(--chart-6))",
  "hsl(var(--chart-7))",
  "hsl(var(--chart-8))",
  "hsl(var(--chart-9))",
  "hsl(var(--chart-10))",
  "hsl(var(--chart-11))",
  "hsl(var(--chart-12))",
  "hsl(var(--chart-13))",
  "hsl(var(--chart-14))",
  "hsl(var(--chart-15))",
  "hsl(var(--chart-16))",
  "hsl(var(--chart-17))",
  "hsl(var(--chart-18))",
  "hsl(var(--chart-19))",
  "hsl(var(--chart-20))",
  "hsl(var(--chart-21))",
  "hsl(var(--chart-22))",
  "hsl(var(--chart-23))",
  "hsl(var(--chart-24))",
  "hsl(var(--chart-25))",
  "hsl(var(--chart-26))",
];

type DiagnosticsTabProps = {
  experiment: Experiment;
};

type DiagnosticsChartRow = {
  runHour: string;
  runLabel: string;
  [seriesKey: string]: number | string;
};

type DiagnosticsSeriesDefinition = {
  key: string;
  label: string;
  color: string;
  memberType: ExperimentDiagnosticsMemberType;
  memberID: number;
  variant: string;
};

type DiagnosticsLegendGroup = {
  key: string;
  control?: DiagnosticsSeriesDefinition;
  pairs: DiagnosticsLegendPair[];
};

type DiagnosticsLegendPair = {
  key: string;
  control?: DiagnosticsSeriesDefinition;
  test: DiagnosticsSeriesDefinition;
};

type DailySpendVariantChartValue = {
  key: string;
  label: string;
  color: string;
  actualSpend: number;
  targetSpend: number;
};

type DailySpendVariantChartGroup = {
  key: string;
  variants: DailySpendVariantChartValue[];
};

type DailySpendChartRow = {
  date: string;
  dayLabel: string;
  chartCeiling: number;
  totalActualSpend: number;
  totalTargetSpend: number;
  isLiveCurrentDay: boolean;
  variants: DailySpendVariantChartValue[];
  variantGroups: DailySpendVariantChartGroup[];
};

type DiagnosticsIssue = {
  kind: "run_metric" | "daily_spend";
  key: string;
  metricId?: number;
  dailySpendId?: number;
  runHour?: string;
  date?: string;
  value?: number;
  actualSpend?: number;
  targetSpend?: number;
  deltaPct?: number;
  actualSharePct?: number;
  expectedSharePct?: number;
  isAcknowledged: boolean;
  acknowledgedByName?: string;
  seriesLabel: string;
  seriesKey?: string;
};

type DiagnosticsChartMarker = {
  key: string;
  leftPct: number;
  title: string;
};

function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    if ("response" in error && error.response && typeof error.response === "object") {
      const response = error.response as { data?: { error?: string; errors?: { message: string }[] } };
      if (response.data?.error) {
        return response.data.error;
      }
      if (response.data?.errors?.[0]?.message) {
        return response.data.errors[0].message;
      }
    }
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
  }
  return "An unexpected error occurred";
}

export function DiagnosticsTab({ experiment }: DiagnosticsTabProps) {
  const [selectedMetricType, setSelectedMetricType] = useState<DiagnosticsCheckType>(DIAGNOSTIC_CHECKS[0].metricType);
  const [selectedLegendPairKeys, setSelectedLegendPairKeys] = useState<string[]>([]);
  const [selectedIssueKeys, setSelectedIssueKeys] = useState<string[]>([]);
  const { data, isLoading, error } = useExperimentDiagnostics(experiment.id ?? null);
  const { trigger: acknowledgeIssue, isMutating: isAcknowledging } = useAcknowledgeExperimentDiagnostic(
    experiment.id ?? null,
  );
  const { trigger: acknowledgeDailySpendIssue, isMutating: isAcknowledgingDailySpend } =
    useAcknowledgeExperimentDiagnosticDailySpend(experiment.id ?? null);
  const { trigger: runDiagnostics, isMutating: isRunningDiagnostics } = useRunExperimentDiagnostics(
    experiment.id ?? null,
  );
  const { data: audienceCampaignData } = useAudienceAndCampaignOptions({ campaign_type: "all" });
  const { data: predictionFlightsData } = usePredictionFlightOptions();
  const { data: experimentCampaigns } = useExperimentCampaigns(
    experiment.type === ExperimentType.Custom ? (experiment.id ?? null) : null,
  );
  const liveWindowNow = useMemo(() => new Date(), [experiment.id]);

  const allSeries = data?.data.series || [];
  const dailySpendSeries = data?.data.daily_spend_series || [];
  const selectedLegendPairKeySet = useMemo(() => new Set(selectedLegendPairKeys), [selectedLegendPairKeys]);

  const campaignNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    const audienceOptions = getAudienceSelectOptions(audienceCampaignData);
    Object.values(audienceOptions).forEach(audience => {
      audience.campaigns.forEach(campaign => {
        map[Number(campaign.id)] = campaign.name;
      });
    });
    return map;
  }, [audienceCampaignData]);

  const predictionFlightNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    predictionFlightsData?.results?.forEach(predictionFlight => {
      map[predictionFlight.id] = predictionFlight.name;
    });
    experiment.import_metadata?.prediction_flights?.forEach(predictionFlight => {
      map[predictionFlight.prediction_flight_id] = predictionFlight.name;
    });
    return map;
  }, [experiment.import_metadata?.prediction_flights, predictionFlightsData]);

  const liveCurrentDayParams = useMemo(
    () => buildCurrentDayDeliveryParamsForExperiment(experiment, liveWindowNow),
    [experiment, liveWindowNow],
  );
  const {
    data: liveCurrentDayDelivery,
    error: liveCurrentDayDeliveryError,
    isLoading: isLoadingLiveCurrentDayDelivery,
  } = useExperimentsDailyDelivery(liveCurrentDayParams, Boolean(liveCurrentDayParams));

  const storedDailySpendDays = useMemo(
    () => buildDiagnosticsDailySpendMetrics(experiment, dailySpendSeries),
    [dailySpendSeries, experiment],
  );
  const liveCurrentDayDate = liveCurrentDayParams?.start_date.split("T")[0];
  const liveCurrentDayMetrics = useMemo(() => {
    if (!liveCurrentDayDate || !liveCurrentDayDelivery) {
      return [];
    }

    return buildDailyDeliveryMetricsForExperiment(
      experiment,
      liveCurrentDayDelivery.rows || [],
      liveCurrentDayDelivery.targets || [],
      [liveCurrentDayDate],
    );
  }, [experiment, liveCurrentDayDate, liveCurrentDayDelivery]);
  const dailySpendDays = useMemo(
    () => mergeDailyDeliveryDays(storedDailySpendDays, liveCurrentDayMetrics),
    [liveCurrentDayMetrics, storedDailySpendDays],
  );
  const dailySpendVariantDefinitions = useMemo<DiagnosticsSeriesDefinition[]>(() => {
    const memberType: ExperimentDiagnosticsMemberType =
      experiment.type === ExperimentType.Global ? "prediction_flight" : "campaign";
    const seen = new Set<string>();
    const definitions: DiagnosticsSeriesDefinition[] = [];

    dailySpendDays.forEach(day => {
      day.variants.forEach(variant => {
        const key = `${variant.variant}:${variant.keyId}`;
        if (seen.has(key)) {
          return;
        }

        seen.add(key);
        definitions.push({
          key,
          label: getSeriesLabel(
            {
              metric_type: "revenue_eur",
              member_type: memberType,
              member_id: variant.keyId,
              variant: variant.variant,
              points: [],
            },
            campaignNameMap,
            predictionFlightNameMap,
          ),
          color: "",
          memberType,
          memberID: variant.keyId,
          variant: variant.variant,
        });
      });
    });

    return withDiagnosticSeriesColors(
      orderLegendItemsByCustomCampaignGroups(definitions, experiment.type, experimentCampaigns || []),
    );
  }, [campaignNameMap, dailySpendDays, experiment.type, experimentCampaigns, predictionFlightNameMap]);
  const visibleDailySpendVariantDefinitions = useMemo(
    () =>
      filterLegendItemsBySelectedPairs(
        dailySpendVariantDefinitions,
        experiment.type,
        experimentCampaigns || [],
        selectedLegendPairKeySet,
      ),
    [dailySpendVariantDefinitions, experiment.type, experimentCampaigns, selectedLegendPairKeySet],
  );
  const dailySpendChartCeiling = useMemo(
    () => buildDailySpendChartCeiling(dailySpendDays, visibleDailySpendVariantDefinitions),
    [dailySpendDays, visibleDailySpendVariantDefinitions],
  );
  const dailySpendChartData = useMemo<DailySpendChartRow[]>(() => {
    return dailySpendDays.map(day => {
      const dayVariantByKey = new Map(day.variants.map(variant => [`${variant.variant}:${variant.keyId}`, variant]));
      const variants = visibleDailySpendVariantDefinitions.map(definition => {
        const variant = dayVariantByKey.get(definition.key);
        return {
          key: definition.key,
          label: definition.label,
          color: definition.color,
          actualSpend: variant?.revenue || 0,
          targetSpend: variant?.targetSpend || 0,
        };
      });

      return {
        date: day.date,
        dayLabel: formatChartDayLabel(day.date),
        chartCeiling: dailySpendChartCeiling,
        totalActualSpend: variants.reduce((sum, variant) => sum + variant.actualSpend, 0),
        totalTargetSpend: variants.reduce((sum, variant) => sum + variant.targetSpend, 0),
        isLiveCurrentDay: day.date === liveCurrentDayDate,
        variants,
        variantGroups: buildDailySpendVariantChartGroups(
          variants,
          visibleDailySpendVariantDefinitions,
          experiment.type,
          experimentCampaigns || [],
        ),
      };
    });
  }, [
    dailySpendChartCeiling,
    dailySpendDays,
    experiment.type,
    experimentCampaigns,
    liveCurrentDayDate,
    visibleDailySpendVariantDefinitions,
  ]);
  const isWaitingForLiveCurrentDay =
    selectedMetricType === "daily_spend" &&
    Boolean(liveCurrentDayParams) &&
    isLoadingLiveCurrentDayDelivery &&
    !liveCurrentDayDelivery;

  const dailySpendIssues = useMemo<DiagnosticsIssue[]>(() => {
    const storedVariantBySeriesAndDate = new Map<
      string,
      { actualSpend: number; actualSharePct: number; expectedSharePct: number }
    >();

    if (experiment.type === ExperimentType.Custom) {
      storedDailySpendDays.forEach(day => {
        day.variants.forEach(variant => {
          storedVariantBySeriesAndDate.set(`${day.date}:${variant.keyId}:${variant.variant}`, {
            actualSpend: variant.revenue,
            actualSharePct: variant.actualSharePct,
            expectedSharePct: variant.trafficAllocationPct,
          });
        });
      });
    }

    return dailySpendSeries.flatMap(series => {
      const seriesLabel = getSeriesLabel(
        {
          metric_type: "revenue_eur",
          member_type: series.member_type,
          member_id: series.member_id,
          variant: series.variant,
          points: [],
        },
        campaignNameMap,
        predictionFlightNameMap,
      );

      return series.points
        .filter(point => point.is_issue)
        .map(point => {
          const customVariantMetrics = storedVariantBySeriesAndDate.get(
            `${point.date.split("T")[0]}:${series.member_id}:${series.variant}`,
          );

          return {
            kind: "daily_spend" as const,
            key: `daily-spend:${point.id}`,
            dailySpendId: point.id,
            date: point.date,
            actualSpend: customVariantMetrics?.actualSpend ?? point.actual_spend,
            targetSpend: point.target_spend,
            deltaPct: point.delta_pct,
            actualSharePct: customVariantMetrics?.actualSharePct,
            expectedSharePct: customVariantMetrics?.expectedSharePct,
            isAcknowledged: point.is_acknowledged,
            acknowledgedByName: point.acknowledged_by_name,
            seriesLabel,
            seriesKey: `${series.variant}:${series.member_id}`,
          };
        });
    });
  }, [campaignNameMap, dailySpendSeries, experiment.type, predictionFlightNameMap, storedDailySpendDays]);

  const checks = useMemo(() => {
    return DIAGNOSTIC_CHECKS.map(check => {
      if (check.metricType === "daily_spend") {
        const hasHistory = dailySpendDays.length > 0;
        const openIssueCount = dailySpendIssues.filter(issue => !issue.isAcknowledged).length;
        const acknowledgedIssueCount = dailySpendIssues.filter(issue => issue.isAcknowledged).length;

        return {
          ...check,
          hasHistory,
          openIssueCount,
          acknowledgedIssueCount,
          passed: hasHistory && openIssueCount === 0,
        };
      }

      const series = allSeries.filter(item => item.metric_type === check.metricType);
      const hasHistory = series.length > 0;
      const openIssueCount = series.reduce((count, item) => {
        return count + item.points.filter(point => isOpenDiagnosticsIssue(point)).length;
      }, 0);
      const acknowledgedIssueCount = series.reduce((count, item) => {
        return count + item.points.filter(point => isAcknowledgedDiagnosticsIssue(point)).length;
      }, 0);

      return {
        ...check,
        hasHistory,
        openIssueCount,
        acknowledgedIssueCount,
        passed: hasHistory && openIssueCount === 0,
      };
    });
  }, [allSeries, dailySpendDays.length, dailySpendIssues]);

  const selectedCheck = checks.find(check => check.metricType === selectedMetricType) || checks[0];

  const selectedSeries = useMemo(
    () =>
      selectedMetricType === "daily_spend" ? [] : allSeries.filter(item => item.metric_type === selectedMetricType),
    [allSeries, selectedMetricType],
  );

  const seriesDefinitions = useMemo<DiagnosticsSeriesDefinition[]>(() => {
    const definitions = selectedSeries.map(series => ({
      key: getDiagnosticsSeriesKey(series),
      label: getSeriesLabel(series, campaignNameMap, predictionFlightNameMap),
      color: "",
      memberType: series.member_type,
      memberID: series.member_id,
      variant: series.variant,
    }));

    return withDiagnosticSeriesColors(
      orderLegendItemsByCustomCampaignGroups(definitions, experiment.type, experimentCampaigns || []),
    );
  }, [campaignNameMap, experiment.type, experimentCampaigns, predictionFlightNameMap, selectedSeries]);
  const visibleSeriesDefinitions = useMemo(
    () =>
      filterLegendItemsBySelectedPairs(
        seriesDefinitions,
        experiment.type,
        experimentCampaigns || [],
        selectedLegendPairKeySet,
      ),
    [experiment.type, experimentCampaigns, selectedLegendPairKeySet, seriesDefinitions],
  );
  const visibleSeriesKeySet = useMemo(
    () => new Set(visibleSeriesDefinitions.map(series => series.key)),
    [visibleSeriesDefinitions],
  );
  const visibleSelectedSeries = useMemo(
    () => selectedSeries.filter(series => visibleSeriesKeySet.has(getDiagnosticsSeriesKey(series))),
    [selectedSeries, visibleSeriesKeySet],
  );

  const chartConfig = useMemo<ChartConfig>(() => {
    return visibleSeriesDefinitions.reduce<ChartConfig>((config, series) => {
      config[series.key] = {
        label: series.label,
        color: series.color,
      };
      return config;
    }, {});
  }, [visibleSeriesDefinitions]);
  const dailySpendChartConfig = useMemo<ChartConfig>(() => {
    return visibleDailySpendVariantDefinitions.reduce<ChartConfig>((config, variant) => {
      config[variant.key] = {
        label: variant.label,
        color: variant.color,
      };
      return config;
    }, {});
  }, [visibleDailySpendVariantDefinitions]);
  const chartData = useMemo<DiagnosticsChartRow[]>(() => {
    const rowsByRunHour = new Map<string, DiagnosticsChartRow>();

    visibleSelectedSeries.forEach(series => {
      const seriesKey = getDiagnosticsSeriesKey(series);
      series.points.forEach(point => {
        const existing = rowsByRunHour.get(point.run_hour) || {
          runHour: point.run_hour,
          runLabel: formatRunLabel(point.run_hour),
        };

        existing[seriesKey] = point.value;
        rowsByRunHour.set(point.run_hour, existing);
      });
    });

    return Array.from(rowsByRunHour.values()).sort(
      (a, b) => new Date(a.runHour).getTime() - new Date(b.runHour).getTime(),
    );
  }, [visibleSelectedSeries]);

  const selectedIssues = useMemo<DiagnosticsIssue[]>(() => {
    if (selectedMetricType === "daily_spend") {
      const visibleDailySpendSeriesKeys = new Set(
        visibleDailySpendVariantDefinitions.map(definition => definition.key),
      );
      return dailySpendIssues
        .filter(
          issue =>
            !selectedLegendPairKeys.length || !issue.seriesKey || visibleDailySpendSeriesKeys.has(issue.seriesKey),
        )
        .sort((a, b) => {
          if (a.isAcknowledged !== b.isAcknowledged) {
            return a.isAcknowledged ? 1 : -1;
          }
          return new Date(b.date || "").getTime() - new Date(a.date || "").getTime();
        });
    }

    return visibleSelectedSeries
      .flatMap(series => {
        const seriesLabel = getSeriesLabel(series, campaignNameMap, predictionFlightNameMap);
        return series.points
          .filter(point => isDiagnosticsIssue(point))
          .map(point => ({
            kind: "run_metric" as const,
            key: String(point.metric_id),
            metricId: point.metric_id,
            runHour: point.run_hour,
            value: point.value,
            isAcknowledged: point.is_acknowledged,
            acknowledgedByName: point.acknowledged_by_name,
            seriesLabel,
            seriesKey: getDiagnosticsSeriesKey(series),
          }));
      })
      .sort((a, b) => {
        if (a.isAcknowledged !== b.isAcknowledged) {
          return a.isAcknowledged ? 1 : -1;
        }
        return new Date(b.runHour).getTime() - new Date(a.runHour).getTime();
      });
  }, [
    campaignNameMap,
    dailySpendIssues,
    predictionFlightNameMap,
    selectedLegendPairKeys.length,
    selectedMetricType,
    visibleDailySpendVariantDefinitions,
    visibleSelectedSeries,
  ]);
  const selectedIssueKeySet = useMemo(() => new Set(selectedIssueKeys), [selectedIssueKeys]);
  const selectedIssuesForChart = useMemo(
    () => selectedIssues.filter(issue => selectedIssueKeySet.has(issue.key)),
    [selectedIssueKeySet, selectedIssues],
  );
  const selectedRunMetricMarkers = useMemo(() => {
    const runHourIndex = new Map(chartData.map((row, index) => [row.runHour, index]));
    const seenRunHours = new Set<string>();
    return selectedIssuesForChart
      .filter(issue => issue.kind === "run_metric" && issue.runHour)
      .filter(issue => {
        const runHour = issue.runHour || "";
        if (seenRunHours.has(runHour)) {
          return false;
        }
        seenRunHours.add(runHour);
        return true;
      })
      .map(issue => {
        const runHour = issue.runHour || "";
        const index = runHourIndex.get(runHour);
        if (index === undefined) {
          return null;
        }

        return {
          key: runHour,
          leftPct: getPointMarkerLeftPct(index, chartData.length),
          title: formatUTCDateTime(runHour),
        };
      })
      .filter((marker): marker is DiagnosticsChartMarker => Boolean(marker));
  }, [chartData, selectedIssuesForChart]);
  const selectedDailySpendMarkers = useMemo(() => {
    const dateIndex = new Map(dailySpendChartData.map((row, index) => [row.date, index]));
    const seenDates = new Set<string>();
    return selectedIssuesForChart
      .filter(issue => issue.kind === "daily_spend" && issue.date)
      .filter(issue => {
        const date = normalizeDiagnosticsIssueDate(issue.date || "");
        if (!date || seenDates.has(date)) {
          return false;
        }
        seenDates.add(date);
        return true;
      })
      .map(issue => {
        const date = normalizeDiagnosticsIssueDate(issue.date || "");
        const index = dateIndex.get(date);
        if (index === undefined) {
          return null;
        }

        return {
          key: date,
          leftPct: getBandMarkerLeftPct(index, dailySpendChartData.length),
          title: formatSpendDateLabel(date),
        };
      })
      .filter((marker): marker is DiagnosticsChartMarker => Boolean(marker));
  }, [dailySpendChartData, selectedIssuesForChart]);

  const handleToggleLegendPair = (pairKey: string) => {
    setSelectedLegendPairKeys(current =>
      current.includes(pairKey) ? current.filter(key => key !== pairKey) : [...current, pairKey],
    );
  };

  const handleClearLegendPairSelection = () => {
    setSelectedLegendPairKeys([]);
  };

  const handleToggleIssueSelection = (issue: DiagnosticsIssue) => {
    setSelectedIssueKeys(current =>
      current.includes(issue.key) ? current.filter(key => key !== issue.key) : [...current, issue.key],
    );
  };

  const handleIssueSelectionKeyDown = (event: KeyboardEvent<HTMLDivElement>, issue: DiagnosticsIssue) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    handleToggleIssueSelection(issue);
  };

  const handleClearIssueSelection = () => {
    setSelectedIssueKeys([]);
  };

  const handleAcknowledge = async (issue: DiagnosticsIssue) => {
    try {
      if (issue.kind === "daily_spend") {
        if (!issue.dailySpendId) {
          return;
        }
        await acknowledgeDailySpendIssue({
          dailySpendId: issue.dailySpendId,
        });
      } else {
        if (!issue.metricId) {
          return;
        }
        await acknowledgeIssue({
          metricId: issue.metricId,
        });
      }
      toast.success("Diagnostics issue acknowledged");
    } catch (acknowledgeError) {
      toast.error("Failed to acknowledge issue", {
        description: getErrorMessage(acknowledgeError),
      });
    }
  };

  const handleRunDiagnostics = async () => {
    try {
      await runDiagnostics();
      toast.success("Diagnostics run completed");
    } catch (runError) {
      toast.error("Failed to run diagnostics", {
        description: getErrorMessage(runError),
      });
    }
  };

  return (
    <section>
      <header className="mb-6 flex flex-col gap-4 border-b border-border pb-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <h3 className="text-lg font-semibold tracking-tight">Diagnostics</h3>
          <p className="text-sm text-muted-foreground">
            Inspect hourly diagnostics by check category. Daily spend uses completed-day bars with stored spend history.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleRunDiagnostics}
          disabled={!experiment.id || isRunningDiagnostics}
          className="shrink-0"
        >
          {isRunningDiagnostics ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {isRunningDiagnostics ? "Running..." : "Run Diagnostics"}
        </Button>
      </header>
      <div>
        {isLoading ? (
          <div className="flex h-64 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            Failed to load diagnostics history.
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[320px,1fr]">
            <div className="space-y-2">
              {checks.map(check => {
                const isSelected = check.metricType === selectedMetricType;

                return (
                  <button
                    key={check.metricType}
                    type="button"
                    onClick={() => {
                      setSelectedMetricType(check.metricType);
                      setSelectedIssueKeys([]);
                    }}
                    className={cn(
                      "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                      isSelected ? "border-primary bg-accent/60" : "border-border hover:bg-accent/40",
                    )}
                  >
                    <span className="mt-0.5 shrink-0">
                      {check.passed ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      )}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground">{check.title}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {getDiagnosticsCheckSummary(
                          check.hasHistory,
                          check.openIssueCount,
                          check.acknowledgedIssueCount,
                        )}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="space-y-4 rounded-xl border border-border p-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">{selectedCheck.chartTitle}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{selectedCheck.description}</p>
              </div>

              {selectedMetricType === "daily_spend" ? (
                isWaitingForLiveCurrentDay ? (
                  <div className="flex h-[360px] items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading current-day spend...
                  </div>
                ) : dailySpendDays.length === 0 ? (
                  <div className="flex h-[360px] items-center justify-center text-sm text-muted-foreground">
                    No completed-day spend history recorded yet.
                  </div>
                ) : visibleDailySpendVariantDefinitions.length === 0 ? (
                  <div className="space-y-3">
                    <div className="flex h-[360px] items-center justify-center text-sm text-muted-foreground">
                      No spend series recorded for the selected pairs.
                    </div>
                    <DiagnosticsSeriesLegend
                      items={dailySpendVariantDefinitions}
                      experimentType={experiment.type}
                      experimentCampaigns={experimentCampaigns || []}
                      selectedPairKeys={selectedLegendPairKeySet}
                      onTogglePair={handleToggleLegendPair}
                      onClearSelection={handleClearLegendPairSelection}
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {liveCurrentDayDeliveryError ? (
                      <div className="text-xs text-muted-foreground">
                        Current-day spend is unavailable right now. Showing completed-day history only.
                      </div>
                    ) : null}
                    <div className="relative">
                      <ChartContainer config={dailySpendChartConfig} className="aspect-auto h-[360px] w-full">
                        <BarChart data={dailySpendChartData} margin={{ top: 8, right: 12, left: 12, bottom: 8 }}>
                          <CartesianGrid vertical={false} />
                          <XAxis dataKey="dayLabel" tickLine={false} axisLine={false} tickMargin={8} minTickGap={20} />
                          <YAxis
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            domain={[0, dailySpendChartCeiling]}
                            tickFormatter={(value: number) => formatDiagnosticsMetricValue("revenue_eur", value)}
                          />
                          <ChartTooltip
                            cursor={false}
                            content={<DailySpendChartTooltip showTargets={experiment.type === ExperimentType.Global} />}
                          />
                          <Bar
                            dataKey="chartCeiling"
                            fill="transparent"
                            isAnimationActive={false}
                            legendType="none"
                            shape={<DailySpendGroupBarShape showTargets={experiment.type === ExperimentType.Global} />}
                          />
                        </BarChart>
                      </ChartContainer>
                      <DiagnosticsChartMarkerOverlay markers={selectedDailySpendMarkers} />
                    </div>
                    <DiagnosticsSeriesLegend
                      items={dailySpendVariantDefinitions}
                      experimentType={experiment.type}
                      experimentCampaigns={experimentCampaigns || []}
                      selectedPairKeys={selectedLegendPairKeySet}
                      onTogglePair={handleToggleLegendPair}
                      onClearSelection={handleClearLegendPairSelection}
                    />
                  </div>
                )
              ) : selectedSeries.length === 0 ? (
                <div className="flex h-[360px] items-center justify-center text-sm text-muted-foreground">
                  No diagnostics runs recorded for this check yet.
                </div>
              ) : visibleSelectedSeries.length === 0 ? (
                <div className="space-y-3">
                  <div className="flex h-[360px] items-center justify-center text-sm text-muted-foreground">
                    No diagnostics runs recorded for the selected pairs.
                  </div>
                  <DiagnosticsSeriesLegend
                    items={seriesDefinitions}
                    experimentType={experiment.type}
                    experimentCampaigns={experimentCampaigns || []}
                    selectedPairKeys={selectedLegendPairKeySet}
                    onTogglePair={handleToggleLegendPair}
                    onClearSelection={handleClearLegendPairSelection}
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <ChartContainer config={chartConfig} className="aspect-auto h-[360px] w-full">
                      <LineChart data={chartData} margin={{ top: 8, right: 12, left: 12, bottom: 8 }}>
                        <CartesianGrid vertical={false} />
                        <XAxis dataKey="runLabel" tickLine={false} axisLine={false} tickMargin={8} minTickGap={20} />
                        <YAxis
                          tickLine={false}
                          axisLine={false}
                          tickMargin={8}
                          tickFormatter={(value: number) =>
                            formatDiagnosticsMetricValue(selectedMetricType as ExperimentDiagnosticsMetricType, value)
                          }
                        />
                        <ChartTooltip
                          cursor={false}
                          content={
                            <RunMetricChartTooltip
                              metricType={selectedMetricType as ExperimentDiagnosticsMetricType}
                              seriesDefinitions={visibleSeriesDefinitions}
                              experimentType={experiment.type}
                              experimentCampaigns={experimentCampaigns || []}
                            />
                          }
                        />
                        {visibleSeriesDefinitions.map(series => (
                          <Line
                            key={series.key}
                            type="linear"
                            dataKey={series.key}
                            name={series.label}
                            stroke={series.color}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                        ))}
                      </LineChart>
                    </ChartContainer>
                    <DiagnosticsChartMarkerOverlay markers={selectedRunMetricMarkers} />
                  </div>
                  <DiagnosticsSeriesLegend
                    items={seriesDefinitions}
                    experimentType={experiment.type}
                    experimentCampaigns={experimentCampaigns || []}
                    selectedPairKeys={selectedLegendPairKeySet}
                    onTogglePair={handleToggleLegendPair}
                    onClearSelection={handleClearLegendPairSelection}
                  />
                </div>
              )}

              <div className="space-y-3 rounded-lg border border-border/70 bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-medium text-foreground">Issues</h4>
                    <p className="text-xs text-muted-foreground">
                      {selectedMetricType === "daily_spend"
                        ? "Daily spend issues are raised only for completed days. Global experiments compare actual vs target spend, and custom experiments compare actual spend share vs configured traffic allocation. Today's bar is live context only."
                        : "Acknowledged issues are ignored in the status icons, but remain visible in the history."}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {selectedIssuesForChart.length > 0 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleClearIssueSelection}
                        className="h-7 px-2 text-xs"
                      >
                        <X className="mr-1 h-3.5 w-3.5" />
                        Clear markers
                      </Button>
                    ) : null}
                    {selectedIssues.length > 0 ? (
                      <Badge variant="outline">
                        {selectedIssues.filter(issue => !issue.isAcknowledged).length} open /{" "}
                        {selectedIssues.filter(issue => issue.isAcknowledged).length} acknowledged
                      </Badge>
                    ) : null}
                  </div>
                </div>

                {selectedIssues.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No issues recorded for this check.</div>
                ) : (
                  <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                    {selectedIssues.map(issue => (
                      <div
                        key={issue.key}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedIssueKeySet.has(issue.key)}
                        onClick={() => handleToggleIssueSelection(issue)}
                        onKeyDown={event => handleIssueSelectionKeyDown(event, issue)}
                        className={cn(
                          "flex cursor-pointer flex-col gap-3 rounded-lg border border-border bg-background px-3 py-3 transition-colors hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex-row md:items-center md:justify-between",
                          selectedIssueKeySet.has(issue.key) && "border-primary bg-primary/5 ring-1 ring-primary/30",
                        )}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            {issue.isAcknowledged ? (
                              <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                            )}
                            <span className="truncate text-sm font-medium text-foreground">{issue.seriesLabel}</span>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {issue.kind === "daily_spend" ? (
                              experiment.type === ExperimentType.Custom ? (
                                <>
                                  {formatSpendDateLabel(issue.date || "")} · Actual spend:{" "}
                                  {formatCurrency(issue.actualSpend || 0)}
                                  {" · "}Actual share: {formatPercent(issue.actualSharePct || 0)}
                                  {" · "}Allocation: {formatPercent(issue.expectedSharePct || 0)} · Delta:{" "}
                                  {formatSignedPercent(issue.deltaPct || 0)}
                                </>
                              ) : (
                                <>
                                  {formatSpendDateLabel(issue.date || "")} · Actual:{" "}
                                  {formatCurrency(issue.actualSpend || 0)}
                                  {" · "}Target: {formatCurrency(issue.targetSpend || 0)} · Delta:{" "}
                                  {formatSignedPercent(issue.deltaPct || 0)}
                                </>
                              )
                            ) : (
                              <>
                                {formatUTCDateTime(issue.runHour || "")} · Value:{" "}
                                {formatDiagnosticsMetricValue(
                                  selectedMetricType as ExperimentDiagnosticsMetricType,
                                  issue.value || 0,
                                )}
                              </>
                            )}
                          </div>
                          {issue.isAcknowledged && (
                            <div className="mt-1 text-xs text-muted-foreground">
                              Acknowledged
                              {issue.acknowledgedByName ? ` by ${issue.acknowledgedByName}` : ""}
                            </div>
                          )}
                        </div>

                        {issue.isAcknowledged ? (
                          <Badge variant="secondary" className="w-fit">
                            Acknowledged
                          </Badge>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isAcknowledging || isAcknowledgingDailySpend}
                            onClick={event => {
                              event.stopPropagation();
                              handleAcknowledge(issue);
                            }}
                            className="w-fit"
                          >
                            {isAcknowledging || isAcknowledgingDailySpend ? "Acknowledging..." : "Acknowledge"}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function DiagnosticsSeriesLegend({
  items,
  experimentType,
  experimentCampaigns,
  selectedPairKeys,
  onTogglePair,
  onClearSelection,
}: {
  items: DiagnosticsSeriesDefinition[];
  experimentType: ExperimentType;
  experimentCampaigns: ExperimentCampaign[];
  selectedPairKeys: Set<string>;
  onTogglePair: (pairKey: string) => void;
  onClearSelection: () => void;
}) {
  if (items.length === 0) {
    return null;
  }

  const canGroupCustomCampaigns =
    experimentType === ExperimentType.Custom &&
    experimentCampaigns.length > 0 &&
    items.every(item => item.memberType === "campaign");

  if (canGroupCustomCampaigns) {
    const groups = buildCustomCampaignLegendGroups(items, experimentCampaigns);
    if (groups.length > 0) {
      return (
        <div className="rounded-md bg-muted/20 px-3 py-2">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {selectedPairKeys.size > 0 ? `${selectedPairKeys.size} selected` : "All pairs"}
            </span>
            {selectedPairKeys.size > 0 ? (
              <Button type="button" variant="ghost" size="sm" onClick={onClearSelection} className="h-6 px-2 text-xs">
                Clear selection
              </Button>
            ) : null}
          </div>
          <div className="max-h-28 overflow-y-auto">
            <div className="grid gap-1.5 lg:grid-cols-2 xl:grid-cols-3">
              {groups.map(group => (
                <div key={group.key} className="min-w-0 space-y-1">
                  {group.pairs.map(pair => (
                    <button
                      key={pair.key}
                      type="button"
                      aria-pressed={selectedPairKeys.has(pair.key)}
                      title={buildPairTitle(pair)}
                      onClick={() => onTogglePair(pair.key)}
                      className={cn(
                        "inline-flex min-w-0 max-w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/60",
                        selectedPairKeys.has(pair.key) && "bg-accent text-foreground ring-1 ring-inset ring-primary/30",
                      )}
                    >
                      {pair.control ? (
                        <DiagnosticsLegendItemView item={pair.control} />
                      ) : (
                        <span className="shrink-0 text-xs font-medium text-amber-700">Unassigned</span>
                      )}
                      <span className="shrink-0 text-xs text-muted-foreground">→</span>
                      <DiagnosticsLegendItemView item={pair.test} />
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="max-h-20 overflow-y-auto rounded-md bg-muted/20 px-3 py-2">
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {items.map(item => (
          <DiagnosticsLegendItemView key={item.key} item={item} />
        ))}
      </div>
    </div>
  );
}

function DiagnosticsLegendItemView({ item }: { item: DiagnosticsSeriesDefinition }) {
  return (
    <span title={item.label} className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-xs">
      <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ backgroundColor: item.color }} aria-hidden="true" />
      <span className="truncate text-muted-foreground">{getShortLegendLabel(item)}</span>
    </span>
  );
}

function buildCustomCampaignLegendGroups(
  items: DiagnosticsSeriesDefinition[],
  experimentCampaigns: ExperimentCampaign[],
): DiagnosticsLegendGroup[] {
  const itemByVariantAndCampaignID = new Map(
    items.map(item => [`${item.variant.toLowerCase()}:${item.memberID}`, item]),
  );
  const consumedKeys = new Set<string>();
  const groups: DiagnosticsLegendGroup[] = [];
  const controls = experimentCampaigns.filter(campaign => campaign.variant === "control");
  const tests = experimentCampaigns.filter(campaign => campaign.variant === "test");

  controls.forEach(controlCampaign => {
    const control = itemByVariantAndCampaignID.get(`control:${controlCampaign.campaign_id}`);
    const pairs = tests
      .filter(testCampaign => testCampaign.control_campaign_id === controlCampaign.campaign_id)
      .map(testCampaign => {
        const test = itemByVariantAndCampaignID.get(`test:${testCampaign.campaign_id}`);
        if (!test) {
          return null;
        }

        return {
          key: getLegendPairKey(controlCampaign.campaign_id, testCampaign.campaign_id),
          control,
          test,
        };
      })
      .filter((pair): pair is DiagnosticsLegendPair => Boolean(pair));

    if (!control && pairs.length === 0) {
      return;
    }

    if (control) {
      consumedKeys.add(control.key);
    }
    pairs.forEach(pair => consumedKeys.add(pair.test.key));

    groups.push({
      key: `control-${controlCampaign.campaign_id}`,
      control,
      pairs,
    });
  });

  const ungroupedItems = items.filter(item => !consumedKeys.has(item.key));
  if (ungroupedItems.length > 0) {
    groups.push({
      key: "ungrouped",
      pairs: ungroupedItems.map(item => ({
        key: getLegendPairKey(undefined, item.memberID),
        test: item,
      })),
    });
  }

  return groups;
}

function buildDailySpendVariantChartGroups(
  variants: DailySpendVariantChartValue[],
  definitions: DiagnosticsSeriesDefinition[],
  experimentType: ExperimentType,
  experimentCampaigns: ExperimentCampaign[],
): DailySpendVariantChartGroup[] {
  const variantByKey = new Map(variants.map(variant => [variant.key, variant]));

  if (
    experimentType !== ExperimentType.Custom ||
    experimentCampaigns.length === 0 ||
    !definitions.every(definition => definition.memberType === "campaign")
  ) {
    return variants.length > 0 ? [{ key: "all", variants }] : [];
  }

  const groups = buildCustomCampaignLegendGroups(definitions, experimentCampaigns)
    .map(group => {
      const variantKeys = [group.control?.key, ...group.pairs.map(pair => pair.test.key)].filter((key): key is string =>
        Boolean(key),
      );
      const seenKeys = new Set<string>();
      const groupVariants = variantKeys
        .filter(key => {
          if (seenKeys.has(key)) {
            return false;
          }
          seenKeys.add(key);
          return true;
        })
        .map(key => variantByKey.get(key))
        .filter((variant): variant is DailySpendVariantChartValue => Boolean(variant));

      return {
        key: group.key,
        variants: groupVariants,
      };
    })
    .filter(group => group.variants.length > 0);

  return groups.length > 0 ? groups : variants.length > 0 ? [{ key: "all", variants }] : [];
}

function withDiagnosticSeriesColors<T extends DiagnosticsSeriesDefinition>(items: T[]) {
  return items.map((item, index) => ({
    ...item,
    color: DIAGNOSTIC_SERIES_COLORS[index % DIAGNOSTIC_SERIES_COLORS.length],
  }));
}

function orderLegendItemsByCustomCampaignGroups<T extends DiagnosticsSeriesDefinition>(
  items: T[],
  experimentType: ExperimentType,
  experimentCampaigns: ExperimentCampaign[],
) {
  if (
    experimentType !== ExperimentType.Custom ||
    experimentCampaigns.length === 0 ||
    !items.every(item => item.memberType === "campaign")
  ) {
    return items;
  }

  const itemByKey = new Map(items.map(item => [item.key, item]));
  const orderedKeys: string[] = [];
  buildCustomCampaignLegendGroups(items, experimentCampaigns).forEach(group => {
    if (group.control) {
      orderedKeys.push(group.control.key);
    }
    group.pairs.forEach(pair => orderedKeys.push(pair.test.key));
  });

  const orderedItems = orderedKeys.map(key => itemByKey.get(key)).filter((item): item is T => Boolean(item));
  const orderedKeySet = new Set(orderedItems.map(item => item.key));
  return [...orderedItems, ...items.filter(item => !orderedKeySet.has(item.key))];
}

function filterLegendItemsBySelectedPairs<T extends DiagnosticsSeriesDefinition>(
  items: T[],
  experimentType: ExperimentType,
  experimentCampaigns: ExperimentCampaign[],
  selectedPairKeys: Set<string>,
) {
  if (
    selectedPairKeys.size === 0 ||
    experimentType !== ExperimentType.Custom ||
    experimentCampaigns.length === 0 ||
    !items.every(item => item.memberType === "campaign")
  ) {
    return items;
  }

  const visibleItemKeys = new Set<string>();
  buildCustomCampaignLegendGroups(items, experimentCampaigns).forEach(group => {
    group.pairs.forEach(pair => {
      if (!selectedPairKeys.has(pair.key)) {
        return;
      }
      if (pair.control) {
        visibleItemKeys.add(pair.control.key);
      }
      visibleItemKeys.add(pair.test.key);
    });
  });

  return items.filter(item => visibleItemKeys.has(item.key));
}

function getLegendPairKey(controlCampaignID: number | undefined, testCampaignID: number) {
  return `control:${controlCampaignID ?? "unassigned"}:test:${testCampaignID}`;
}

function buildPairTitle(pair: DiagnosticsLegendPair) {
  if (!pair.control) {
    return pair.test.label;
  }
  return `${pair.control.label} with ${pair.test.label}`;
}

function getShortLegendLabel(item: DiagnosticsSeriesDefinition) {
  const variantLabel = toTitleCase(item.variant);
  if (item.memberType === "campaign") {
    return `${variantLabel} ${item.memberID}`;
  }

  return `${variantLabel} PF ${item.memberID}`;
}

type DailySpendGroupBarShapeProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: DailySpendChartRow;
  showTargets?: boolean;
};

type DailySpendChartTooltipProps = {
  active?: boolean;
  payload?: Array<{ payload?: DailySpendChartRow }>;
  showTargets: boolean;
};

type RunMetricChartTooltipProps = {
  active?: boolean;
  payload?: Array<{
    dataKey?: string | number;
    name?: string | number;
    value?: string | number;
    payload?: DiagnosticsChartRow;
  }>;
  metricType: ExperimentDiagnosticsMetricType;
  seriesDefinitions: DiagnosticsSeriesDefinition[];
  experimentType: ExperimentType;
  experimentCampaigns: ExperimentCampaign[];
};

function buildDailySpendChartCeiling(
  days: { variants: Array<{ keyId: number; variant: string; revenue: number; targetSpend: number }> }[],
  visibleDefinitions: DiagnosticsSeriesDefinition[],
) {
  const visibleKeys = new Set(visibleDefinitions.map(definition => definition.key));
  const maxValue = Math.max(
    ...days.flatMap(day =>
      day.variants
        .filter(variant => visibleKeys.has(`${variant.variant}:${variant.keyId}`))
        .flatMap(variant => [variant.revenue, variant.targetSpend]),
    ),
    1,
  );

  const roughStep = maxValue / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalizedStep = roughStep / magnitude;

  let step = magnitude;
  if (normalizedStep > 1 && normalizedStep <= 2) {
    step = 2 * magnitude;
  } else if (normalizedStep > 2 && normalizedStep <= 2.5) {
    step = 2.5 * magnitude;
  } else if (normalizedStep > 2.5 && normalizedStep <= 5) {
    step = 5 * magnitude;
  } else if (normalizedStep > 5) {
    step = 10 * magnitude;
  }

  return step * 4;
}

function DailySpendGroupBarShape({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  payload,
  showTargets = false,
}: DailySpendGroupBarShapeProps) {
  if (!payload || width <= 0 || height <= 0) {
    return null;
  }

  const groups =
    payload.variantGroups.length > 0 ? payload.variantGroups : [{ key: "all", variants: payload.variants }];
  const totalVariantCount = groups.reduce((count, group) => count + group.variants.length, 0);
  if (totalVariantCount === 0) {
    return null;
  }

  const groupGap = groups.length > 1 ? Math.min(12, width * 0.06) : 0;
  const inGroupGap = totalVariantCount > groups.length ? Math.min(5, width * 0.025) : 0;
  const inGroupGapCount = groups.reduce((count, group) => count + Math.max(group.variants.length - 1, 0), 0);
  const slotWidth = Math.max(
    (width - groupGap * (groups.length - 1) - inGroupGap * inGroupGapCount) / totalVariantCount,
    0,
  );

  let currentX = x;

  return (
    <g>
      {groups.flatMap((group, groupIndex) =>
        group.variants.map((variant, variantIndex) => {
          const slotX = currentX;
          currentX +=
            slotWidth +
            (variantIndex < group.variants.length - 1 ? inGroupGap : groupIndex < groups.length - 1 ? groupGap : 0);
          const targetHeight =
            showTargets && payload.chartCeiling > 0 ? (variant.targetSpend / payload.chartCeiling) * height : 0;
          const actualHeight = payload.chartCeiling > 0 ? (variant.actualSpend / payload.chartCeiling) * height : 0;
          const actualWidth = showTargets ? Math.max(slotWidth * 0.42, 4) : slotWidth;
          const actualX = slotX + (slotWidth - actualWidth) / 2;

          return (
            <g key={`${payload.date}-${group.key}-${variant.key}`}>
              {showTargets && variant.targetSpend > 0 ? (
                <rect
                  x={slotX}
                  y={y + height - targetHeight}
                  width={slotWidth}
                  height={targetHeight}
                  rx={2}
                  fill={variant.color}
                  fillOpacity={0.18}
                />
              ) : null}
              {variant.actualSpend > 0 ? (
                <rect
                  x={actualX}
                  y={y + height - actualHeight}
                  width={actualWidth}
                  height={actualHeight}
                  rx={2}
                  fill={variant.color}
                />
              ) : null}
            </g>
          );
        }),
      )}
    </g>
  );
}

function DiagnosticsChartMarkerOverlay({ markers }: { markers: DiagnosticsChartMarker[] }) {
  if (markers.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute bottom-8 left-[72px] right-3 top-2">
      {markers.map(marker => (
        <div
          key={marker.key}
          title={marker.title}
          className="absolute bottom-0 top-0 border-l-2 border-dashed border-destructive"
          style={{ left: `${marker.leftPct}%` }}
        />
      ))}
    </div>
  );
}

function DailySpendChartTooltip({ active, payload, showTargets }: DailySpendChartTooltipProps) {
  const row = payload?.[0]?.payload;
  if (!active || !row) {
    return null;
  }

  return (
    <div className="rounded-lg border border-border/70 bg-background px-3 py-2 shadow-sm">
      <div className="text-xs font-semibold text-foreground">
        {formatSpendDateLabel(row.date)}
        {row.isLiveCurrentDay ? " · Live current day" : ""}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">
        {showTargets ? (
          <>
            Total: {formatCurrency(row.totalActualSpend)} / {formatCurrency(row.totalTargetSpend)}
          </>
        ) : (
          <>Total: {formatCurrency(row.totalActualSpend)}</>
        )}
      </div>
      <DailySpendTooltipRows row={row} showTargets={showTargets} />
    </div>
  );
}

function DailySpendTooltipRows({ row, showTargets }: { row: DailySpendChartRow; showTargets: boolean }) {
  const groups = row.variantGroups.length > 0 ? row.variantGroups : [{ key: "all", variants: row.variants }];
  const shouldGroup = groups.length > 1 || groups.some(group => group.variants.length > 1);

  if (!shouldGroup) {
    return (
      <div className="mt-2 space-y-1.5">
        {row.variants.map(variant => (
          <DailySpendTooltipVariantRow key={variant.key} variant={variant} showTargets={showTargets} />
        ))}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      {groups.map(group => (
        <div key={group.key} className="space-y-1 border-l-2 border-border/70 pl-2">
          {group.variants.map(variant => (
            <DailySpendTooltipVariantRow key={variant.key} variant={variant} showTargets={showTargets} />
          ))}
        </div>
      ))}
    </div>
  );
}

function DailySpendTooltipVariantRow({
  variant,
  showTargets,
}: {
  variant: DailySpendVariantChartValue;
  showTargets: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 text-[11px]">
      <div className="min-w-0">
        <span
          className="mr-2 inline-block h-2.5 w-2.5 rounded-[2px] align-middle"
          style={{ backgroundColor: variant.color }}
          aria-hidden="true"
        />
        <span className="truncate text-foreground">{variant.label}</span>
      </div>
      <div className="shrink-0 font-mono tabular-nums text-foreground">
        {showTargets ? (
          <>
            {formatCurrency(variant.actualSpend)} / {formatCurrency(variant.targetSpend)}
          </>
        ) : (
          <>{formatCurrency(variant.actualSpend)}</>
        )}
      </div>
    </div>
  );
}

function RunMetricChartTooltip({
  active,
  payload,
  metricType,
  seriesDefinitions,
  experimentType,
  experimentCampaigns,
}: RunMetricChartTooltipProps) {
  const row = payload?.[0]?.payload;
  if (!active || !row || !payload?.length) {
    return null;
  }

  const payloadByKey = new Map(payload.map(item => [String(item.dataKey || item.name || ""), item]));
  const visibleDefinitions = seriesDefinitions.filter(definition => payloadByKey.has(definition.key));

  return (
    <div className="rounded-lg border border-border/70 bg-background px-3 py-2 shadow-sm">
      <div className="text-xs font-semibold text-foreground">{formatUTCDateTime(row.runHour)}</div>
      <RunMetricTooltipRows
        definitions={visibleDefinitions}
        payloadByKey={payloadByKey}
        metricType={metricType}
        experimentType={experimentType}
        experimentCampaigns={experimentCampaigns}
      />
    </div>
  );
}

function RunMetricTooltipRows({
  definitions,
  payloadByKey,
  metricType,
  experimentType,
  experimentCampaigns,
}: {
  definitions: DiagnosticsSeriesDefinition[];
  payloadByKey: Map<string, { value?: string | number }>;
  metricType: ExperimentDiagnosticsMetricType;
  experimentType: ExperimentType;
  experimentCampaigns: ExperimentCampaign[];
}) {
  const canGroup =
    experimentType === ExperimentType.Custom &&
    experimentCampaigns.length > 0 &&
    definitions.every(definition => definition.memberType === "campaign");

  if (!canGroup) {
    return (
      <div className="mt-2 space-y-1.5">
        {definitions.map(definition => (
          <RunMetricTooltipSeriesRow
            key={definition.key}
            definition={definition}
            value={payloadByKey.get(definition.key)?.value}
            metricType={metricType}
          />
        ))}
      </div>
    );
  }

  const groups = buildCustomCampaignLegendGroups(definitions, experimentCampaigns)
    .map(group => {
      const definitionKeys = [group.control?.key, ...group.pairs.map(pair => pair.test.key)].filter(
        (key): key is string => Boolean(key),
      );
      const seenKeys = new Set<string>();
      const groupDefinitions = definitionKeys
        .filter(key => {
          if (seenKeys.has(key)) {
            return false;
          }
          seenKeys.add(key);
          return true;
        })
        .map(key => definitions.find(definition => definition.key === key))
        .filter((definition): definition is DiagnosticsSeriesDefinition => Boolean(definition));

      return {
        key: group.key,
        definitions: groupDefinitions,
      };
    })
    .filter(group => group.definitions.length > 0);

  return (
    <div className="mt-2 space-y-2">
      {groups.map(group => (
        <div key={group.key} className="space-y-1 border-l-2 border-border/70 pl-2">
          {group.definitions.map(definition => (
            <RunMetricTooltipSeriesRow
              key={definition.key}
              definition={definition}
              value={payloadByKey.get(definition.key)?.value}
              metricType={metricType}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function RunMetricTooltipSeriesRow({
  definition,
  value,
  metricType,
}: {
  definition: DiagnosticsSeriesDefinition;
  value?: string | number;
  metricType: ExperimentDiagnosticsMetricType;
}) {
  return (
    <div className="flex items-center justify-between gap-4 text-[11px]">
      <div className="min-w-0">
        <span
          className="mr-2 inline-block h-2.5 w-2.5 rounded-[2px] align-middle"
          style={{ backgroundColor: definition.color }}
          aria-hidden="true"
        />
        <span className="truncate text-foreground">{definition.label}</span>
      </div>
      <span className="shrink-0 font-mono tabular-nums text-foreground">
        {formatDiagnosticsMetricValue(metricType, Number(value || 0))}
      </span>
    </div>
  );
}

function getSeriesLabel(
  series: ExperimentDiagnosticsSeries,
  campaignNameMap: Record<number, string>,
  predictionFlightNameMap: Record<number, string>,
) {
  const baseName = getMemberLabel(series.member_type, series.member_id, campaignNameMap, predictionFlightNameMap);
  return `${toTitleCase(series.variant)} · ${baseName}`;
}

function getMemberLabel(
  memberType: ExperimentDiagnosticsMemberType,
  memberID: number,
  campaignNameMap: Record<number, string>,
  predictionFlightNameMap: Record<number, string>,
) {
  if (memberType === "campaign") {
    return campaignNameMap[memberID] || `Campaign ${memberID}`;
  }

  return predictionFlightNameMap[memberID] || `Prediction Flight ${memberID}`;
}

function toTitleCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatRunLabel(runHour: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hourCycle: "h23",
  }).format(new Date(runHour));
}

function formatSpendDateLabel(date: string) {
  const parsedDate = new Date(date.includes("T") ? date : `${date}T00:00:00Z`);
  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(parsedDate);
}

function normalizeDiagnosticsIssueDate(date: string) {
  return date.split("T")[0];
}

function getPointMarkerLeftPct(index: number, count: number) {
  if (count <= 1) {
    return 50;
  }
  return (index / (count - 1)) * 100;
}

function getBandMarkerLeftPct(index: number, count: number) {
  if (count <= 0) {
    return 50;
  }
  return ((index + 0.5) / count) * 100;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatSignedPercent(value: number) {
  const abs = Math.abs(value).toFixed(2);
  if (value > 0) {
    return `+${abs}%`;
  }
  if (value < 0) {
    return `-${abs}%`;
  }
  return "0.00%";
}

function formatPercent(value: number) {
  return `${value.toFixed(2)}%`;
}
